<template>
  <v-card
    :loading="isProcessing"
    :disabled="isProcessing"
    width="600"
    class="mx-auto mt-16 py-3"
    rounded="lg"
  >
    <div class="d-flex justify-end mb-4 mr-3">
      <v-icon @click="$router.back()">mdi-close</v-icon>
    </div>
    <error-alert :message.sync="initialFetchError"></error-alert>
    <v-alert
      type="success"
      dismissible
      v-model="requestSuccess"
    >Updated Vehicle</v-alert>
    <div
      class="px-3 px-md-10"
      v-if="!initialFetchError"
    >
      <v-card-title class="pl-0">{{ isEdit ? "Edit Vehicle" : "Add Vehicle" }}</v-card-title>
      <v-form
        v-model="valid"
        ref="form"
      >
        <v-text-field
          label="Year"
          v-model="vehicle.year"
          outlined
          :rules="yearRules"
          type="number"
          :min="0"
          dense
        ></v-text-field>
        <v-text-field
          label="Make"
          v-model="vehicle.make"
          :rules="requiredRuleOnly"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          label="Model"
          v-model="vehicle.model"
          :rules="requiredRuleOnly"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          label="Engine Size"
          v-model="vehicle.engine_size"
          outlined
          suffix="-L"
          :rules="engineSizeRules"
          type="number"
          :min="0"
          :step="0.1"
          dense
        ></v-text-field>
        <v-autocomplete
          v-model="vehicle.catalytic_converters"
          :items="suggestionConvertersCodes"
          :loading="isCatalyticConvertersLoading"
          :search-input.sync="searchCatalyticConvertersInput"
          outlined
          multiple
          cache-items
          chips
          deletable-chips
          clearable
          hide-no-data
          hide-selected
          label="Select Converters"
          placeholder="Start typing to Search"
        ></v-autocomplete>
        <v-textarea
          outlined
          label="Notes"
          v-model="vehicle.notes"
        ></v-textarea>
      </v-form>
      <error-alert
        :message.sync="requestError"
        :validationMessages="requestValidationErrors"
      ></error-alert>
      <v-row class="mt-5">
        <v-col v-if="isEdit">
          <v-btn
            block
            color="error"
            @click="resetClicked"
          >Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn
            block
            color="success"
            @click="validateAndAddOrEditVehicle"
          >{{ isEdit ? "Update" : "Add"}}</v-btn>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import { extractDataAxios, extractErrorMessageAxios, extractValidationErrorsAxios } from "@/httpClient";
import { mapMutations } from "@/store/modules/vehicles";

const requiredRule = (value) => !!value || "Required";

export default {
  props: {
    isEdit: Boolean,
    id: [String, Number],
  },

  data: () => ({
    valid: false,
    vehicle: {
      make: null,
      model: null,
      year: null,
      engine_size: null,
      notes: null,
      catalytic_converters: [],
    },
    requiredRuleOnly: [requiredRule],
    yearRules: [requiredRule, (v) => /^[1-9]\d*$/.test(v) || "Year must be greater than zero"],
    engineSizeRules: [
      requiredRule,
      (v) =>
        (/^\d+(\.\d)?$/.test(v) && v > 0) ||
        "Engine Size must be greater than zero and should not have more than one decimal",
    ],

    isProcessing: false,
    requestError: null,
    requestSuccess: false,
    requestValidationErrors: null,
    initialFetchError: null,
    initialData: null,
    suggestionConvertersCodes: [],
    isCatalyticConvertersLoading: false,
    searchCatalyticConvertersInput: null,
    searchCatalyticConvertersTimeoutToken: null,
  }),

  created: function () {
    if (this.isEdit) {
      if (!this.id) {
        this.initialFetchError = "Missing Vehicle id to be edited";
        return;
      }
      this.fetchData();
    }
  },

  watch: {
    searchCatalyticConvertersInput(val) {
      val && val !== this.vehicle.catalytic_converters && this.querySelections(val);
    },
  },

  methods: {
    ...mapMutations({
      ADD_VEHICLE: "ADD",
      EDIT_VEHICLE: "EDIT",
    }),
    querySelections(v) {
      this.isCatalyticConvertersLoading = true;

      if (this.searchCatalyticConvertersTimeoutToken) clearTimeout(this.searchCatalyticConvertersTimeoutToken);

      this.searchCatalyticConvertersTimeoutToken = setTimeout(async () => {
        try {
          const data = extractDataAxios(
            await window.axios.get("/api/catalytic-converters/converter-search", {
              params: { term: v },
            })
          );
          if (data) {
            this.suggestionConvertersCodes = this.catalyticConvertersToSelectList(data);
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.isCatalyticConvertersLoading = false;
        }
      }, 1500);
    },
    async fetchData() {
      this.isProcessing = true;
      try {
        const data = extractDataAxios(await window.axios.get("/api/vehicles/" + this.id));
        if (data) {
          for (let key of Object.keys(data)) {
            if (key === "catalytic_converters") {
              if (data[key]) {
                this.suggestionConvertersCodes = this.catalyticConvertersToSelectList(data[key]);
                this.vehicle[key] = this.catalyticConvertersToIds(data[key]);
                continue;
              }
            }
            if (data[key]) {
              this.vehicle[key] = data[key];
            }
          }

          this.initialData = { ...this.vehicle };
          this.isProcessing = false;
        }
      } catch (e) {
        console.error(e);
        this.isProcessing = false;
        this.initialFetchError = "Failed to fetch the vehicle. Error: " + extractErrorMessageAxios(e);
      }
    },

    resetClicked() {
      this.vehicle = { ...this.initialData };
    },

    validateAndAddOrEditVehicle() {
      this.$refs.form.validate();
      if (this.valid) {
        if (this.isEdit) {
          this.editVehicle();
        } else {
          this.addNewVehicle();
        }
      }
    },

    async addNewVehicle() {
      this.isProcessing = true;
      try {
        const data = extractDataAxios(await window.axios.post("/api/vehicles", this.vehicle));
        if (data) {
          this.ADD_VEHICLE(data);
          this.$router.push({ name: "vehicles" });
        }
      } catch (e) {
        console.error(e);
        this.requestError = "Adding new vehicle failed. Error: " + extractErrorMessageAxios(e);
        this.requestValidationErrors = extractValidationErrorsAxios(e);
      } finally {
        this.isProcessing = false;
      }
    },

    async editVehicle() {
      this.isProcessing = true;
      try {
        const data = extractDataAxios(await window.axios.put("/api/vehicles/" + this.id, this.vehicle));
        if (data) {
          this.EDIT_VEHICLE(data);
          for (let key of Object.keys(data)) {
            if (key === "catalytic_converters") {
              if (data[key]) {
                this.suggestionConvertersCodes = this.catalyticConvertersToSelectList(data[key]);
                this.vehicle[key] = this.catalyticConvertersToIds(data[key]);
                continue;
              }
            }
            if (data[key]) {
              this.vehicle[key] = data[key];
            }
          }
          this.initialData = { ...this.vehicle };
          this.requestSuccess = true;
        }
      } catch (e) {
        console.error(e);
        this.requestError = "Editing converter failed. Error: " + extractErrorMessageAxios(e);
        this.requestValidationErrors = extractValidationErrorsAxios(e);
      } finally {
        this.isProcessing = false;
      }
    },

    catalyticConvertersToSelectList(converters) {
      return converters.map((converter) => ({
        text: `${converter.code} - ${converter.manufacturer}`,
        value: converter.id,
      }));
    },
    catalyticConvertersToIds(converters) {
      return converters.map((converter) => converter.id);
    },
  },
};
</script>
